import { useState, useEffect } from 'react';

import { FormattedMessage, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { Box, xcss } from '@atlaskit/primitives';
import Inline from '@atlaskit/primitives/inline';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import RelativeDate from 'src/components/date/components/relative-date';
import { useStatsigGate } from 'src/contexts/feature-provider';
import urls from 'src/sections/repository/urls';
import { getCurrentUserUuid } from 'src/selectors/user-selectors';
import {
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';
import { useQuery } from 'src/utils/fb-query';
import { captureMessageForResponse } from 'src/utils/sentry';
import store from 'src/utils/store';

const messages = defineMessages({
  body: {
    id: 'frontbucket.repository.recently-pushed-branch-banner.body',
    defaultMessage: 'You recently pushed changes to {branchName}',
    description: 'Body for the recently pushed branch banner',
  },
  action: {
    id: 'frontbucket.repository.recently-pushed-branch-banner.action',
    defaultMessage: 'Create pull request',
    description: 'Action for creating a pull request',
  },
  dismiss: {
    id: 'frontbucket.repository.recently-pushed-branch-banner.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Dismiss button for the recently pushed branch banner',
  },
});

type Props = {
  repositoryOwner: string;
  repositorySlug: string;
  source: string;
};

const fetchRecentlyPushedBranches = async ({
  owner,
  slug,
}: {
  owner: string;
  slug: string;
}) => {
  const url = urls.api.internal.recentlyPushedBranches(owner, slug);
  const response = await fetch(url);

  if (!response.ok) {
    captureMessageForResponse(
      response,
      'Failed to fetch recently pushed branches'
    );
    throw new Error('Failed to fetch recently pushed branches');
  }
  return response.json();
};

const RecentlyPushedBranchBanner = ({
  repositoryOwner,
  repositorySlug,
  source,
}: Props): JSX.Element | null => {
  const currentUserUuid = useSelector(getCurrentUserUuid);

  const isBannerEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcRecentlyPushedBranchesBanner
  );

  const { data, isPending, isError } = useQuery(
    fetchRecentlyPushedBranches,
    {
      owner: repositoryOwner,
      slug: repositorySlug,
    },
    {
      enabled: !!currentUserUuid && isBannerEnabled,
    }
  );

  const latestBranchPush = data?.branches?.[0];
  const lastPushTimestamp = latestBranchPush?.timestamp;
  const repoSlug = repositorySlug;
  const storageKey = `recently-pushed-branch-banner::${currentUserUuid}::${repoSlug}`;
  const [dismissed, setDismissed] = useState(true);
  const shouldRenderBanner =
    !!currentUserUuid &&
    !dismissed &&
    isBannerEnabled &&
    !isPending &&
    !isError &&
    data &&
    data.branches &&
    data.branches.length > 0 &&
    !!latestBranchPush;

  useEffect(() => {
    if (!lastPushTimestamp) return;
    const storedTimestamp = store.get(storageKey);

    let isDismissed = false;
    if (typeof storedTimestamp === 'string') {
      const storedDate = new Date(storedTimestamp);
      const lastPushDate = new Date(lastPushTimestamp);
      if (!isNaN(storedDate.getTime()) && !isNaN(lastPushDate.getTime())) {
        isDismissed = storedDate.getTime() >= lastPushDate.getTime();
      }
    }
    setDismissed(isDismissed);
  }, [lastPushTimestamp, storageKey]);

  useEffect(() => {
    if (shouldRenderBanner) {
      publishScreenEvent('recentlyPushedBranchBanner', {
        source,
      });
    }
  }, [shouldRenderBanner, source]);

  if (!shouldRenderBanner) return null;

  const handleDismiss = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'dismissRecentlyPushedBranchBanner',
      source,
    });
    store.set(storageKey, lastPushTimestamp);
    setDismissed(true);
  };

  const handleCreatePrClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'createPullRequestFromRecentBranch',
      source,
    });
  };

  const createPrUrl = urls.ui.createPullRequest(
    repositoryOwner,
    repositorySlug,
    latestBranchPush.branch.name
  );

  return (
    <Box xcss={xcss({ marginBottom: 'space.300' })}>
      <SectionMessage
        isDismissible
        onDismiss={handleDismiss}
        actions={[
          <SectionMessageAction
            href={createPrUrl}
            key="create-pr"
            onClick={handleCreatePrClick}
          >
            <FormattedMessage {...messages.action} />
          </SectionMessageAction>,
          <SectionMessageAction onClick={handleDismiss} key="dismiss">
            <FormattedMessage {...messages.dismiss} />
          </SectionMessageAction>,
        ]}
      >
        <Inline space="space.200" alignBlock="center">
          <FormattedMessage
            {...messages.body}
            values={{
              branchName: <strong>{latestBranchPush.branch.name}</strong>,
            }}
          />
          <RelativeDate date={latestBranchPush.timestamp} />
        </Inline>
      </SectionMessage>
    </Box>
  );
};

export { RecentlyPushedBranchBanner };
